// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * pieces.ts — the brick shapes and how they sit in the world.
 *
 * A brick is a small set of unit CELLS (tetromino-ish, plus a couple of simple
 * planks so a tower is buildable). It is NOT a grid piece: these cells only
 * describe the rigid body's outline. Once dropped it is a planck (Box2D) compound
 * body and never snaps to anything again.
 *
 * `cellsAt(shape, rot)` returns the cell offsets rotated by 0/90/180/270°,
 * re-centred on the shape's own centroid so rotation happens about the middle
 * (which is also where the compound body's centre of mass sits).
 */

import { makeRng } from '@ben-gy/game-engine/rng';

export interface Shape {
  id: string;
  /** Unit-cell offsets, integer grid, before centring. */
  cells: ReadonlyArray<readonly [number, number]>;
  /** Palette tint index. */
  tint: number;
}

// The classic seven, plus a domino and a plank. Tints spread across the palette.
export const SHAPES: readonly Shape[] = [
  { id: 'I', tint: 1, cells: [[0, 0], [1, 0], [2, 0], [3, 0]] },
  { id: 'O', tint: 0, cells: [[0, 0], [1, 0], [0, 1], [1, 1]] },
  { id: 'T', tint: 3, cells: [[0, 0], [1, 0], [2, 0], [1, 1]] },
  { id: 'L', tint: 0, cells: [[0, 0], [0, 1], [0, 2], [1, 2]] },
  { id: 'J', tint: 2, cells: [[1, 0], [1, 1], [1, 2], [0, 2]] },
  { id: 'S', tint: 4, cells: [[1, 0], [2, 0], [0, 1], [1, 1]] },
  { id: 'Z', tint: 2, cells: [[0, 0], [1, 0], [1, 1], [2, 1]] },
  { id: 'D', tint: 4, cells: [[0, 0], [1, 0]] },            // domino
  { id: 'P', tint: 3, cells: [[0, 0], [1, 0], [2, 0]] },    // plank
];

export const SHAPE_BY_ID: Record<string, Shape> = Object.fromEntries(SHAPES.map((s) => [s.id, s]));

/** Cell offsets rotated by `rot` quarter-turns, centred on the centroid (cell units). */
export function cellsAt(shape: Shape, rot: number): Array<[number, number]> {
  const r = ((rot % 4) + 4) % 4;
  const rotated = shape.cells.map(([x, y]) => {
    switch (r) {
      case 1: return [-y, x] as [number, number];
      case 2: return [-x, -y] as [number, number];
      case 3: return [y, -x] as [number, number];
      default: return [x, y] as [number, number];
    }
  });
  // Centre on the centroid so rotation pivots about the middle and the world
  // body's centre of mass matches the visual centre.
  let sx = 0;
  let sy = 0;
  for (const [x, y] of rotated) { sx += x; sy += y; }
  const cx = sx / rotated.length;
  const cy = sy / rotated.length;
  return rotated.map(([x, y]) => [x - cx, y - cy]);
}

/** Width/height of a shape at a rotation, in cell units (for aim clamping). */
export function shapeExtent(shape: Shape, rot: number): { w: number; h: number } {
  const cells = cellsAt(shape, rot);
  const xs = cells.map(([x]) => x);
  const ys = cells.map(([, y]) => y);
  // +1 because each cell is one unit wide/tall about its own centre.
  return {
    w: Math.max(...xs) - Math.min(...xs) + 1,
    h: Math.max(...ys) - Math.min(...ys) + 1,
  };
}

/**
 * A deterministic brick order from the seed: a shuffled 7-bag of tetrominoes
 * (so no long droughts), with a domino or plank spliced in every few bricks to
 * keep a tower buildable. Pure function of (seed, index) — every peer agrees.
 */
export function brickAt(seed: number, index: number): Shape {
  // Every 4th brick is a "helper" (domino/plank), alternating.
  if (index % 4 === 3) {
    const rnd = makeRng((seed ^ 0x9e3779b1 ^ (index * 2654435761)) >>> 0);
    return rnd() < 0.5 ? SHAPE_BY_ID.D : SHAPE_BY_ID.P;
  }
  const tetro = ['I', 'O', 'T', 'L', 'J', 'S', 'Z'];
  // Ordinal of this brick among the tetrominoes (helpers sit at index%4===3, and
  // floor(index/4) counts exactly the helpers before a non-helper index).
  const bagIndex = index - Math.floor(index / 4);
  const bag = bagIndex % 7;
  const bagNo = Math.floor(bagIndex / 7);
  // Shuffle this bag deterministically.
  const order = shuffle7(seed + bagNo * 101);
  return SHAPE_BY_ID[tetro[order[bag]]];
}

function shuffle7(seed: number): number[] {
  const rnd = makeRng(seed >>> 0);
  const a = [0, 1, 2, 3, 4, 5, 6];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}